import { v4 as uuidv4 } from 'uuid'
import { EMPTY, catchError, defer, exhaustMap, filter, firstValueFrom, from, map, mergeMap, of, withLatestFrom } from 'rxjs'
import type { TRepositories } from '../../index.ts'
import { orderWizardActions as wizard } from '../../Slice/OrderWizard/OrderWizardSlice.ts'
import type { ResumableOrder } from '../../Slice/OrderWizard/OrderWizardSlice.ts'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { IUser } from '../../../Model/index.ts'
import { draftFromOrder } from '../../../Services/OrderWizard/resume.ts'
import { storedOrderIds } from '../../../Services/OrderWizard/draftStorage.ts'
import { guarded } from './guarded.ts'
import { messageOf } from './messages.ts'
import { fetchOrderState } from './orderState.ts'
import type { RootState, WizardEpic } from './draftState.ts'

/* the newest few: an order left longer is better found in the classic list */
const RESUMABLE_LIMIT = 5

/** Orders that belong to some draft of the user's, in this tab or in browser storage. */
export const ordersOfDrafts = (state: RootState, user: IUser | undefined): Set<string> =>
  new Set([
    ...Object.values(state.orderWizard.drafts).flatMap((draft) => (draft.orderId ? [draft.orderId] : [])),
    ...(user ? storedOrderIds(user.id) : [])
  ])

const unsubmittedOrders = (repository: TRepositories['OrderRepository'], user: IUser, size: number) =>
  firstValueFrom(
    repository.getAllOrders(
      {
        size,
        page: 0,
        filters: [
          { value: user.id, parameter: 'createdByUser', filterType: 'EQUAL' },
          { value: '', parameter: 'orderSubmission', filterType: 'IS_NULL' }
        ]
      },
      user
    )
  ).then((found) => (found?.content ?? []).map((entry) => entry.order).filter((order) => !order.orderSubmission && !order.deletedAt))

export const resumableOrdersEpic: WizardEpic = (action$, state$, { OrderRepository }) =>
  action$.pipe(
    filter(wizard.resumableOrdersRequested.match),
    withLatestFrom(state$),
    exhaustMap(([, state]) => {
      const user = state.authentication.user
      if (!user) {
        return EMPTY
      }
      return from(unsubmittedOrders(OrderRepository, user, RESUMABLE_LIMIT + 5)).pipe(
        withLatestFrom(state$),
        map(([orders, latest]) => {
          const held = ordersOfDrafts(latest, user)
          const resumable: ResumableOrder[] = orders
            .filter((order) => !held.has(order.id))
            .slice(0, RESUMABLE_LIMIT)
            .map((order) => ({ orderId: order.id, name: order.name, ...(order.orderCreated ? { createdAt: order.orderCreated } : {}) }))
          return wizard.resumableOrdersLoaded({ orders: resumable })
        }),
        catchError((error: unknown) => {
          console.warn('Order wizard: could not list unsubmitted orders', error)
          return EMPTY
        })
      )
    })
  )

/**
 * An unsubmitted order as the active draft: the draft that holds it, else one built from the server.
 */
export const resumeOrderEpic: WizardEpic = (action$, state$, repositories) =>
  action$.pipe(
    filter(wizard.resumeRequested.match),
    withLatestFrom(state$),
    exhaustMap(
      guarded(
        'resuming an order',
        ([action, state]: [ReturnType<typeof wizard.resumeRequested>, RootState]) => {
          const { orderId } = action.payload
          const holder = Object.values(state.orderWizard.drafts).find((draft) => draft.orderId === orderId)
          if (holder) {
            return of(wizard.activeDraftSet({ draftId: holder.id }), wizard.resumeNoticeDismissed())
          }
          return defer(() => fetchOrderState(orderId, repositories)).pipe(
            mergeMap(({ order, snapshot, platforms }) => {
              const draft: DraftState | undefined = order && draftFromOrder({ order, snapshot, platforms }, uuidv4(), new Date().toISOString())
              if (!draft) {
                const why = snapshot.order === 'missing' ? 'the order no longer exists' : 'the order has been submitted'
                return of(wizard.resumeFailed({ orderId, error: `It can’t be resumed: ${why}.` }))
              }
              return of(wizard.draftResumed({ draft }))
            }),
            catchError((error: unknown) => of(wizard.resumeFailed({ orderId, error: `Resuming failed: ${messageOf(error)}` })))
          )
        },
        ([action], error) => wizard.resumeFailed({ orderId: action.payload.orderId, error })
      )
    )
  )

/** Deletes the order of a discarded draft, as the classic view does, but only while nothing is submitted. */
export const deleteDiscardedOrderEpic: WizardEpic = (action$, state$, { OrderRepository }) =>
  action$.pipe(
    filter(wizard.discardedOrderDeleteRequested.match),
    withLatestFrom(state$),
    exhaustMap(
      guarded(
        'deleting a discarded order',
        ([action, state]: [ReturnType<typeof wizard.discardedOrderDeleteRequested>, RootState]) => {
          const { orderId } = action.payload
          return OrderRepository.getOrder(orderId).pipe(
            mergeMap((order) =>
              order.orderSubmission
                ? of(wizard.discardedOrderDeleteFinished({ orderId, error: 'It has been submitted, so it stays.' }))
                : OrderRepository.deleteOrder({ orderId }, state.authentication.user).pipe(
                    map(() => wizard.discardedOrderDeleteFinished({ orderId }))
                  )
            ),
            catchError((error: unknown) =>
              of(wizard.discardedOrderDeleteFinished({ orderId, error: `Deleting the order failed: ${messageOf(error)}` }))
            )
          )
        },
        ([action], error) => wizard.discardedOrderDeleteFinished({ orderId: action.payload.orderId, error })
      )
    )
  )
